/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
 */
import cloneDeep from 'lodash.clonedeep';
import {translate} from '@ion/core/src/locale/locale';
import {
  AugmentedBrakeData,
  AugmentedSmarterTrailerSensorData,
  AugmentedSmarterTrailerSensorProperty,
  BaseAsset,
  BrakeChamberPosition,
  BrakeChamberStatusType,
  BrakeTypeProps,
  sensorBatteryThreshold,
  SensorTag,
  SensorTypes,
  SensorUnits,
  SmarterTrailerIssueTypes,
  SmarterTrailerSensorData,
  SmarterTrailerSensorProperties,
  TireAndWheelDisplayMapping,
  TrailerDoorStatus,
  trailerPSISensorDataMapping,
  trailerSensorDataMapping,
  TrailerStatus
} from '@ion/core/src/types/entities';
import {getUserUnit, isImperial} from '@ion/core/src/units/helpers';
import {Unit} from '@ion/core/src/units/units';
import {
  sortByString
} from '@ion/core/src/utils/ArrayUtil';
import {
  isTrue
} from '@ion/core/src/utils/BooleanUtils';
import {
  convertPressure,
  psiToKpa
} from '@ion/core/src/utils/Conversions';
import {
  isObjectEmpty
} from '@ion/core/src/utils/ObjectUtil';
import {
  contains
} from '@ion/core/src/utils/StringUtil';
import {convertPressureValueAndUnits, getParkingDragStatus} from '@ion/dls/src/components/smarterTrailer/helper';

const smarterTrailerIssues = Object.values(SmarterTrailerIssueTypes);

const PARKING_BRAKE_ENGAGEMENT_THRESHOLD_PSI = 65;

/**
 *  Takes a deeply nested object of sensor data and makes sense of it in a way that
 *  is more easily readable and digestible for a component. More easily associates
 *  readings to it's respective sensor. Preserves the raw data in a rawData property.
 *
 * ```typescript
 *  import {augmentSensorData} from '@ion/features/src/smarterTrailer/SmarterTrailerUtils';
 *
 *  augmentSensorData(sensorData)
 * ```
 */
export const augmentSensorData = (
  device: BaseAsset,
  sensorData: SmarterTrailerSensorData[],
  merge: boolean
): AugmentedSmarterTrailerSensorData => {
  const preferredSystem = getUserUnit();

  if (sensorData) {
    const sensorDataCopy = cloneDeep(sensorData);
    const augmentedData: AugmentedSmarterTrailerSensorData  = {
      [SensorTypes.TPMS]       : {},
      [SensorTypes.WETS]       : {},
      [SensorTypes.DoorState]  : {},
      [SensorTypes.CargoSense] : {},
      [SensorTypes.BMS]        : {},
      [SensorTypes.WMS]        : {},
      [SensorTypes.LMS]        : {}
    };

    const brakeData: AugmentedBrakeData = {
      frontLeft          : {},
      frontRight         : {},
      rearLeft           : {},
      rearRight          : {},
      parkingBrakeStatus : null
    };

    sensorDataCopy.forEach(({sensorData}) => {
      if (sensorData?.data) {
        const {sensorType, data, ...rest} = sensorData;
        sensorData.data.forEach(({
          details = [],
          sensorTag,
          sensorId
        }) => {
          if (sensorType === SensorTypes.DoorState) {
            if (isObjectEmpty(augmentedData[sensorType])) {
              augmentedData[sensorType] = {
                doorHistory : [],
                ...rest
              };
            }
            const {messageTime} = sensorData;
            const doorHistory   = augmentedData[sensorType].doorHistory;
            const exists        = doorHistory.find(entry => {
              return entry.messageTime === messageTime;
            });
            if (!exists) {
              const entry                     = {messageTime};
              const {DoorState, BatteryLevel} = SmarterTrailerSensorProperties;
              const {Open, Closed}            = TrailerDoorStatus;
              const {Normal, Low}             = TrailerStatus;
              details.forEach(({label, value}) => {
                if (contains(label, DoorState)) {
                  entry[DoorState] = (value === 'O' ? Open : Closed);
                } else if (contains(label, BatteryLevel)) {
                  entry[BatteryLevel] = (value === 'N' ? Normal : Low);
                }
              });
              doorHistory.push(entry);
            }
            sortByString(doorHistory, 'messageTime', true);
            return;
          }
          if (sensorType === SensorTypes.BMS) {
            if (isObjectEmpty(augmentedData[sensorType])) {
              augmentedData[sensorType] = {
                data : {},
                ...rest
              };
            }
            details.forEach((sensorDetail) => {
              if (sensorDetail) {
                const label = sensorDetail.label;
                const value = parseInt(sensorDetail.value);
                if (label === BrakeChamberPosition.parkingBrakeStatus) { // parking brake status
                  // By default, brake status will be 'released' isDragAlert will be false
                  brakeData[label] = isNaN(value)
                    ? BrakeChamberStatusType.NotAvailable
                    : BrakeChamberStatusType.Released;
                  brakeData.isDragAlert = false;
                } else if (Object.values(BrakeChamberPosition).includes(label)) { // brake wear
                  const location = label;
                  let status = BrakeChamberStatusType.Normal;
                  if (value <= 10) {
                    status = BrakeChamberStatusType.Warning;
                  } else if (value <= 20) {
                    status = BrakeChamberStatusType.Caution;
                  }
                  brakeData[location] = {
                    ...brakeData[location],
                    wear : {
                      value,
                      status
                    }
                  };
                } else { // brake pressure
                  const preferredUnit = preferredSystem === Unit.METRIC ? SensorUnits.KPA : SensorUnits.PSI;
                  const {
                    units: pressureUnits,
                    value: pressureValue
                  } = convertPressureValueAndUnits(sensorDetail, preferredUnit);
                  const [row, position, brakeProperty] = label.split(/(?=[A-Z])/);
                  const location = `${row}${position}`;
                  brakeData[location] = {
                    ...brakeData[location],
                    [brakeProperty.toLowerCase()] : {
                      value     : pressureValue,
                      units     : pressureUnits,
                      isEngaged : getParkingEngaged(pressureValue, pressureUnits)
                    }
                  };
                }
              }
            });
          }
          if (sensorType === SensorTypes.CargoSense) {
            const itemValue     = augmentedData[sensorType];
            const itemProperty  = sensorId;
            itemValue[itemProperty] = {
              sensorTag,
              ...rest
            };
            details.forEach(({
              label,
              value
            }) => {
              const zoneData = itemValue?.[itemProperty];
              zoneData.sensorId = sensorId;
              if (label.includes('Height')) {
                zoneData.height = {value, label};
              }
              if (label.includes('Status')) {
                zoneData.status = {value, label};
              }
            });
            return;
          }
          if (sensorType === SensorTypes.WMS) {
            const itemValue     = augmentedData[sensorType || 'WE1'];
            const itemProperty  = sensorId || 'WE1';

            itemValue[itemProperty] = {
              sensorTag,
              ...rest
            };

            const weightData = itemValue?.[itemProperty];
            details.forEach(({
              label,
              value,
              units
            }) => {
              if (label.includes('weightValue')) {
                weightData.value = value;
                weightData.units = units;
              }
            });
            return;
          }
          let sensorMapping = trailerSensorDataMapping;
          if (sensorType === SensorTypes.TPMS && sensorTag === SensorTag.TD2) {
            sensorMapping = trailerPSISensorDataMapping as any;
          }
          const locationValue = details.find(({label}) => {
            return label === SmarterTrailerSensorProperties.WheelPosition;
          });
          const wheelLocation = sensorMapping?.[locationValue?.value];
          const itemValue     = augmentedData[sensorType];
          const itemProperty  = [locationValue?.value ?? sensorTag];
          itemValue[itemProperty] = {
            sensorTag,
            ...(wheelLocation && {wheelDisplayName: TireAndWheelDisplayMapping[wheelLocation]}),
            ...(wheelLocation && {wheelLocation}),
            ...(sensorId && {sensorId}),
            ...rest
          };
          details.forEach(({
            label : detailLabel,
            value : detailValue,
            units : detailUnits,
            type  : detailType
          }) => {
            const wheelData        = itemValue?.[itemProperty];
            wheelData[detailLabel] = detailValue;
            if (SmarterTrailerSensorProperties.BatteryVoltage.includes(detailType)) {
              wheelData.batteryVoltage = {
                value : detailValue,
                units : detailUnits
              };
            }
            smarterTrailerIssues.forEach(issue => {
              if (detailLabel === issue) {
                wheelData[detailLabel] = isTrue(detailValue);
              }
            });
            if (detailLabel.includes(SmarterTrailerSensorProperties.Pressure)) {
              wheelData[detailLabel] = {
                value : detailValue,
                units : detailUnits
              };
            }
            if (detailLabel === SmarterTrailerSensorProperties.Temperature) {
              wheelData[detailLabel] = {
                value : detailValue,
                units : detailUnits
              };
            }
          });
        });
      }
    });

    const brakePressureAverages = {
      Service : 0,
      Parking : 0
    };
    Object.entries(brakeData).forEach(([location, data]) => {
      if (data) {
        const {parking, service} = data;
        brakePressureAverages.Parking += parking?.value || 0;
        brakePressureAverages.Service += service?.value || 0;
      }
    });
    Object.entries(brakePressureAverages).forEach(([type, sum]) => {
      brakePressureAverages[type] = sum / 4;
    });

    //logic to handle modifying status for BMS pressure gauges based on average of all 4 brake chambers
    Object.entries(brakeData).forEach(([location, data]) => {
      if (data) {
        const {parking, service} = data;
        if (parking?.value) {
          brakeData[location].parking.status = getBrakePressureStatus(brakePressureAverages.Parking, parking);
          brakeData[location].service.status = getBrakePressureStatus(brakePressureAverages.Service, service);
          /**
           * When we loop through parking data for each brake chamber, update status if meeting business logic
           * - 0-65PSI = Parking Brake Engaged
           * - 0-65PSI and asset speed > 5, set isDragAlert to true
           * - >65PSI = Parking Brake Release
           */
          if (getParkingEngaged(parking.value, parking.units)) {
            brakeData.parkingBrakeStatus = BrakeChamberStatusType.Engaged;
            brakeData.isDragAlert        = getParkingDragStatus(brakeData.parkingBrakeStatus, device);
          }
        }
      }
    });

    augmentedData[SensorTypes.BMS].data = brakeData;
    augmentedData.rawData = sensorDataCopy;
    return augmentedData;
  }
  return null;
};

export const getParkingEngaged = (value, units) => {
  const threshold = units === SensorUnits.PSI
    ? PARKING_BRAKE_ENGAGEMENT_THRESHOLD_PSI
    : convertPressure(PARKING_BRAKE_ENGAGEMENT_THRESHOLD_PSI, SensorUnits.KPA);
  return value <= threshold;
};

const getBrakePressureStatus = (avg, {value, units}) => {
  const deviation = Math.abs(value - avg);
  const threshold = units === SensorUnits.PSI ? 10 : psiToKpa(10);
  return deviation > threshold
    ? BrakeChamberStatusType.Warning
    : BrakeChamberStatusType.Normal;
};

const mergeSensorData = (
  previousSensorData: AugmentedSmarterTrailerSensorData,
  nextSensorData: AugmentedSmarterTrailerSensorData
): AugmentedSmarterTrailerSensorData => {
  const mergedObject: AugmentedSmarterTrailerSensorData = {
    [SensorTypes.TPMS]       : {},
    [SensorTypes.WETS]       : {},
    [SensorTypes.DoorState]  : {},
    [SensorTypes.CargoSense] : {},
    [SensorTypes.BMS]        : {},
    [SensorTypes.WMS]        : {},
    [SensorTypes.LMS]        : {}
  };
  mergedObject.DoorState = !isObjectEmpty(nextSensorData.DoorState)
    ? nextSensorData.DoorState : previousSensorData?.DoorState || {};
  // Update Cargo Sense
  mergedObject.CargoSense = !isObjectEmpty(nextSensorData.CargoSense)
    ? nextSensorData.CargoSense : previousSensorData?.CargoSense || {};
  // Update TPMS
  mergedObject.TPMS = !isObjectEmpty(nextSensorData.TPMS)
    ? nextSensorData.TPMS : previousSensorData?.TPMS || {};
  // Update WETS
  mergedObject.WETS = !isObjectEmpty(nextSensorData.WETS)
    ? nextSensorData.WETS : previousSensorData?.WETS || {};
  // Update BMS
  mergedObject.BMS = !isObjectEmpty(nextSensorData.BMS)
    ? nextSensorData.BMS : previousSensorData?.BMS || {};
  // Update WMS
  mergedObject.WMS = !isObjectEmpty(nextSensorData.WMS)
    ? nextSensorData.WMS : previousSensorData?.WMS || {};
  // Update LMS
  mergedObject.LMS = !isObjectEmpty(nextSensorData.LMS)
    ? nextSensorData.LMS : previousSensorData?.LMS || {};
  // Update Raw Data
  mergedObject.rawData = !isObjectEmpty(nextSensorData.rawData)
    ? nextSensorData.rawData : previousSensorData?.rawData || [];
  return mergedObject;
};

/**
 *  Loops through a trailer wheel object provided by augment function above
 *  and returns an array with trailer names that have some kind of issue.
 *
 * ```typescript
 *  import {getTrailerIssues} from '@ion/features/src/smarterTrailer';
 *
 *  getTrailerIssues(trailerWheel)
 * ```
 */
export const getTrailerIssues = (
  trailerWheel: AugmentedSmarterTrailerSensorProperty
): any[] => {
  const issues = [];
  Object.values(trailerWheel).forEach(trailer => {
    const {highPressure, highTemperature, lowPressure, highPressureAlarm, lowPressureAlarm, temperatureAlarm} = trailer;
    const hasIssues = Boolean(
      highPressure ||
      highTemperature ||
      lowPressure ||
      lowPressureAlarm ||
      highPressureAlarm ||
      temperatureAlarm
    );
    if (hasIssues) {
      issues.push(trailer.wheelLocation);
    }
  });
  return issues;
};

/**
 *  Finds percentage of number compared to max volts value and returns the whole
 *  percentage float to one decimal. Expects a volts unit
 *
 * ```typescript
 *  import {getSensorBatteryPercentage} from '@ion/features/src/smarterTrailer';
 *
 *  getSensorBatteryPercentage('6.56') // 53.7
 *  getSensorBatteryPercentage('6.56', 'kJ') // false
 * ```
 */
export const getSensorBatteryPercentageVolts = (value: string | number, units = 'V'): number => {
  if (units === 'V') {
    const parsedVal = parseFloat(value as string);
    const {maximumV: {value: maxBatteryVolts}} = sensorBatteryThreshold;
    // truncate number to one decimal and return the float
    return parseFloat(((parsedVal / maxBatteryVolts) * 100).toFixed(1));
  }
};

/**
 *  Finds total percentage of availability across all zones, with any number of
 *  zones in the passed cargo sense object
 *
 * ```typescript
 *  import {calculateTotalZoneAvailability} from '@ion/features/src/smarterTrailer';
 *
 *  calculateTotalZoneAvailability({
 *    0 : {
 *      height : {
 *        value: '70'
 *      }
 *    },
 *   1 : {
 *      height : {
 *        value: '45'
 *      }
 *    },
 *   2 : {
 *      height : {
 *        value: '20'
 *      }
 *    }
 *  }) // 55
 * ```
 */
export const calculateTotalZoneAvailability = (cargoSenseData: AugmentedSmarterTrailerSensorData['CargoSense']): number => {
  if (!isObjectEmpty(cargoSenseData)) {
    const values = [];
    const zoneTotals = Object.entries(cargoSenseData).reduce((acc, [key, val]) => {
      const height = val?.height?.value;
      if (key !== 'undefined' && height) {
        values.push(height);
        return acc + parseInt(height);
      }
      return acc;
    }, 0);
    return Math.round(100 - ((zoneTotals / (100 * values.length)) * 100));
  }
  return 0;
};

/**
 *  Utility function to convert KPA to PSI and
 *  return the value as string with 2 decimal points
 *
 * ```typescript
 *
 *  kpaToPsi(790.277) // 114.62
 * ```
 */
export const kpaToPsi = (kpaValue: number): string => (kpaValue * 0.145038).toFixed(2);

/**
 *  Utility function to convert celsius to Fahrenheit
 *
 * ```typescript
 *
 *  celsiusToFahrenheit(55.55) // 131.99
 * ```
 */
export const celsiusToFahrenheit = (celsiusValue: number): number => {
  return Math.round(((celsiusValue * 1.8000) + 32.00) * 10) /10;
}
  ;

/**
 *  Utility function to convert Fahrenheit to celsius
 *
 * ```typescript
 *
 *  fahrenheitToCelsius(100) // 37.7778
 * ```
 */
export const fahrenheitToCelsius = (fahrenheitValue: number): number => {
  return Math.round(((fahrenheitValue - 32) / 1.8) * 10) / 10;
};

/**
 *  Convert metric measures and units to imperial measures and units
 *  for Temperature and Pressure
 *
 * ```typescript
 *  import {getImperialMeasures} from '@ion/features/src/smarterTrailer';
 *
 *  getImperialMeasures('55.55', 'C', 'US') // '131.99 F'
 *  getImperialMeasures('790.277', 'KPA', 'US') // '114.62 PSI'
 * ```
 */
export const getImperialMeasures = (
  measuredValue: string,
  measuredUnit: string,
  userPreferredRegion: string
): string => {
  if (isImperial(userPreferredRegion as Unit)) {
    if (measuredUnit === SensorUnits.C) {
      return `${celsiusToFahrenheit(Number(measuredValue))} ${SensorUnits.F}`;
    } else {
      return `${kpaToPsi(Number(measuredValue))} ${SensorUnits.PSI.toUpperCase()}`;
    }
  } else {
    return `${measuredValue} ${measuredUnit}`;
  }
};

/**
 *  Aggregate of zones not having height for 'H' type sensor tag
 *  having cargo sense data
 *
 * ```typescript
 *  import {getMissingZones} from '@ion/features/src/smarterTrailer';
 *
 *  getMissingZones({
 *    0 : {
 *      height : {
 *        value: '70'
 *      },
 *      sensorId: 0
 *    },
 *   1 : {
 *      height : {
 *        value: '45'
 *      },
 *      sensorId: 1
 *    },
 *   2 : {
 *      height : {
 *        value: '20'
 *      },
 *      sensorId: 0
 *    }
 *  }, 'Zone') // 55
 * ```
 */
export const getMissingZones = (
  cargoSenseData: AugmentedSmarterTrailerSensorData['CargoSense'],
  key: string
): string => {
  const items = Object.entries(cargoSenseData);
  const invalidZones = items.filter(([, {height}]) => {
    return !height || isNaN(parseInt(height.value));
  });
  return invalidZones.map(([, {sensorId}]) => {
    return `${key} ${sensorId}`;
  }).join(', ');
};

export const getBMSDataTable = (data: AugmentedBrakeData, preferredUnit): BrakeTypeProps[][] => {
  return Object.entries(data).map(([brakeChamber, chamberData]) => {
    if (brakeChamber !== BrakeChamberPosition.parkingBrakeStatus && brakeChamber !== BrakeChamberPosition.isDragAlert) {
      const position = BrakeChamberPosition[brakeChamber];
      const {
        service: {
          units: serviceUnits,
          value: serviceValue,
          status: serviceStatus
        },
        parking: {
          units:  parkingUnits,
          value:  parkingValue,
          status: parkingStatus
        },
        wear : {
          value : wearValue,
          status: wearStatus
        }
      } = chamberData || {};

      const {
        value: convertedServiceValue,
        units: convertedServiceUnits
      } = convertPressureValueAndUnits(
        {
          value : serviceValue,
          units : serviceUnits
        },
        preferredUnit
      );

      const {
        value: convertedParkingValue,
        units: convertedParkingUnits
      } = convertPressureValueAndUnits(
        {
          value : parkingValue,
          units : parkingUnits
        },
        preferredUnit
      );

      return [{
        value  : translate(`common:smarterTrailer.brakeChamber.position.${position}`),
        status : BrakeChamberStatusType.Normal
      }, {
        value  : convertedParkingValue ? convertedParkingValue + convertedParkingUnits : '-',
        status : parkingStatus

      }, {
        value  : convertedServiceValue ? convertedServiceValue + convertedServiceUnits : '-',
        status : serviceStatus
      }, {
        value  : isNaN(wearValue) ? '-' : wearValue + '%',
        status : wearStatus
      }];
    }
  });
};
